import { useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

const usePagination = (defaultPageSize = 10, { persist = true } = {}) => {
  const location = useLocation();

  // Generate path-scoped storage key
  const storageKey = useMemo(() => `pagination:${location.pathname}`, [location.pathname]);

  // Initialize from sessionStorage or defaults
  const [pagination, setPaginationState] = useState(() => {
    if (!persist) {
      return { current: 1, pageSize: defaultPageSize, total: 0 };
    }

    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          current: parsed.current || 1,
          pageSize: parsed.pageSize || defaultPageSize,
          total: 0,
        };
      } catch (e) {
        return { current: 1, pageSize: defaultPageSize, total: 0 };
      }
    }
    return { current: 1, pageSize: defaultPageSize, total: 0 };
  });

  const setPagination = useCallback(
    (updates) => {
      setPaginationState((prev) => {
        const partialState = typeof updates === "function" ? updates(prev) : updates;
        const mergedState = { ...prev, ...partialState };

        const pageSizeChanged = mergedState.pageSize !== prev.pageSize;
        const nextState = {
          ...mergedState,
          current: pageSizeChanged ? 1 : mergedState.current,
        };
        if (persist) {
          const toStore = {
            current: nextState.current,
            pageSize: nextState.pageSize,
          };
          sessionStorage.setItem(storageKey, JSON.stringify(toStore));
        }

        return nextState;
      });
    },
    [storageKey],
  );

  // Handle table change event
  const handleChange = useCallback(
    (paginationInfo, filters, sorter) => {
      setPagination({
        current: paginationInfo.current,
        pageSize: paginationInfo.pageSize,
      });
    },
    [setPagination],
  );

  // Reset pagination to first page (useful for filter changes)
  const resetPage = useCallback(() => {
    setPagination({ current: 1 });
  }, [setPagination]);

  return {
    pagination,
    setPagination,
    handleChange,
    resetPage,
  };
};

export default usePagination;
